import RatingOverlaySelect from "../elements/RatingOverlaySelect";
import ToggleCell from "../elements/ToggleCell";
import {
   applyChecklistRatingDigit,
   formatChecklistRatingDisplay,
   isOverlayRatingInput,
   isValidChecklistRating,
   resolveRatingInput,
   sanitizeChecklistRating,
   type RatingInputMode,
} from "./checklistRating";

type AdjectiveProps = {
   id: string;
   name: string;
   description: string;
   value: { r: number; c: string };
   setValue: (childId: string, value: unknown) => void;
   showValidations?: boolean;
   isAdmin?: boolean;
   ratingInput?: RatingInputMode;
   elements?: unknown;
   section?: unknown;
   formId?: number;
   rowClassName?: string;
   tabIndex?: number;
};

export default function Adjective(props: AdjectiveProps) {
   const ratingInput = props.ratingInput ?? resolveRatingInput(props.elements);
   const isSkill = (props.value.c === "S" || props.value.c === "D");
   const isTrait = (props.value.c === "T" || props.value.c === "D");
   const isChecklistInput = ratingInput === "input";
   const currentRating = Number(props.value.r || 0);
   const checklistRatingInvalid = props.showValidations && !isValidChecklistRating(currentRating);

   function onClickTrait() {
      const t = !isTrait;
      const newValue = t ? (isSkill ? "D" : "T") : (isSkill? "S": "");
      props.setValue(props.id+'c', newValue);
   }
   
   function onClickSkill() {
      const s = !isSkill
      const newValue = s ? (isTrait ? "D" : "S") : (isTrait? "T": "");
      props.setValue(props.id+'c', newValue);
   }

   function onClickRating(value: number) {
      props.setValue(props.id + 'r', value);
   }

   function commitChecklistRating(value: unknown) {
      const normalized = sanitizeChecklistRating(value);
      if (normalized === currentRating) {
         return;
      }

      props.setValue(props.id + "r", normalized);
   }

   function onChecklistRatingKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (allowedKeys.includes(e.key)) {
         if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault();
            commitChecklistRating("");
         }
         return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
         e.preventDefault();
         return;
      }

      if (!/^[0-9]$/.test(e.key)) {
         e.preventDefault();
         return;
      }

      // Admin mode uses section-level keyboard handling in Checklist.tsx.
      if (props.isAdmin) return;

      e.preventDefault();
      const currentValue = String(e.currentTarget.value || "");
      const nextValue = applyChecklistRatingDigit(currentValue, e.key);
      commitChecklistRating(nextValue);
   }

   function onChecklistRatingPaste(e: React.ClipboardEvent<HTMLInputElement>) {
      e.preventDefault();
      const pasted = e.clipboardData?.getData("text") ?? "";
      const digits = pasted.replace(/\D/g, "");
      if (!digits) {
         return;
      }

      let display = String(e.currentTarget.value || "");
      for (const digit of digits) {
         const nextValue = applyChecklistRatingDigit(display, digit);
         display = formatChecklistRatingDisplay(nextValue);
         commitChecklistRating(nextValue);
         if (nextValue === 10) {
            break;
         }
      }
   }

   function onChecklistRatingWheel(e: React.WheelEvent<HTMLInputElement>) {
      e.currentTarget.blur();
   }

   function onChecklistRatingChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value;
      if (raw === "") {
         commitChecklistRating("");
         return;
      }

      if (!/^[0-9]+$/.test(raw)) {
         return;
      }

      commitChecklistRating(raw);
   }

   function onDropdownChange(e: React.ChangeEvent<HTMLSelectElement>) {
      const raw = e.target.value;
      if (raw === "") {
         commitChecklistRating("");
         return;
      }

      onClickRating(Number(raw));
   }

   function renderRatingControl() {
      if (isChecklistInput) {
         return (
            <input
               placeholder=""
               type="text"
               inputMode="numeric"
               autoComplete="off"
               maxLength={2}
               className={`checklistRatingInput${checklistRatingInvalid ? " invalid" : ""}`}
               value={formatChecklistRatingDisplay(currentRating)}
               tabIndex={props.tabIndex}
               onChange={onChecklistRatingChange}
               onKeyDown={onChecklistRatingKeyDown}
               onPaste={onChecklistRatingPaste}
               onWheel={onChecklistRatingWheel}
            />
         );
      }

      if (ratingInput === "dropdown") {
         return (
            <select
               className={`checklistRatingSelect${checklistRatingInvalid ? " invalid" : ""}`}
               value={isValidChecklistRating(currentRating) ? String(currentRating) : ""}
               tabIndex={props.tabIndex}
               onChange={onDropdownChange}
            >
               <option value="" />
               {Array.from({ length: 10 }, (_, index) => index + 1).map((optionValue) => (
                  <option key={optionValue} value={optionValue}>{optionValue}</option>
               ))}
            </select>
         );
      }

      if (isOverlayRatingInput(ratingInput)) {
         return (
            <RatingOverlaySelect
               value={currentRating}
               orientation={ratingInput}
               showValidations={props.showValidations}
               tabIndex={props.tabIndex}
               onSelect={onClickRating}
            />
         );
      }

      return null;
   }

   return (
      <tr key={props.id} className={props.rowClassName || undefined}>
         <td className="rating center" data-column="1">
            {renderRatingControl()}
         </td>
         <td className="textColumn term columndata" data-column="2">{props.name}</td>
         <td className="textColumn definition columndata" data-column="3">{props.description}</td>
         <ToggleCell value={isTrait} valid={isTrait || isSkill} toggle={onClickTrait} showValidations={props.showValidations} className="T" />
         <ToggleCell value={isSkill} valid={isTrait || isSkill} toggle={onClickSkill} showValidations={props.showValidations} className="S" />
      </tr >
   );
}
